// Components/SchoolCreationModal.js
import React, { useState } from 'react';
import superAdminAPI from '../services/superAdminAPI';
import { AlertTriangle, Building2, CheckCircle, GitBranch, HelpCircle, Info, Loader, Mail, Plus, Trash2, User, X } from 'lucide-react';

const SchoolCreationModal = ({ isOpen, onClose, onSchoolCreated }) => {
    // School type: 'single' for standalone, 'group' for school group with branches
    const [schoolType, setSchoolType] = useState('single');

    // Branches for group mode
    const [branches, setBranches] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        subscription_plan: 'trial',
        max_users: 50,
        address: {
            city: '',
            state: '',
            country: '',
            postal_code: '',
            street_address: ''
        },
        contact_info: {
            email: '',
            phone: '',
            contact_person: ''
        },
        admin_name: '',
        admin_email: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(0); // Start at step 0 for school type selection

    // Branch management functions
    const addBranch = () => {
        setBranches([...branches, {
            name: '',
            admin_name: '',
            admin_email: '',
            domain: ''
        }]);
    };

    const removeBranch = (index) => {
        setBranches(branches.filter((_, i) => i !== index));
    };

    const updateBranch = (index, field, value) => {
        const updatedBranches = [...branches];
        updatedBranches[index][field] = value;
        setBranches(updatedBranches);
    };

    const getTotalSteps = () => schoolType === 'group' ? 4 : 3;

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.includes('.')) {
            const [section, field] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const validateStep1 = () => {
        if (!formData.name) return "School name is required";
        if (!formData.domain) return "Domain is required";
        return null;
    };

    const validateStep2 = () => {
        if (!formData.contact_info.email) return "Contact email is required";
        if (!formData.contact_info.contact_person) return "Contact person is required";
        return null;
    };

    const validateStep3 = () => {
        if (!formData.admin_name) return "Admin name is required";
        if (!formData.admin_email) return "Admin email is required";

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.admin_email)) {
            return "Please enter a valid admin email address";
        }

        return null;
    };

    const nextStep = () => {
        let error = null;

        if (step === 1) {
            error = validateStep1();
        } else if (step === 2) {
            error = validateStep2();
        }

        if (error) {
            setError(error);
            return;
        }

        setError(null);
        setStep(step + 1);
    };

    const prevStep = () => {
        setError(null);
        setStep(step - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationError = validateStep3();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let response;

            if (schoolType === 'single') {
                // Single school creation (existing behavior)
                response = await superAdminAPI.createSchool(formData);
            } else {
                // School group creation
                const groupData = {
                    group_name: formData.name,
                    parent_admin_name: formData.admin_name,
                    parent_admin_email: formData.admin_email,
                    domain: formData.domain,
                    address: formData.address,
                    contact_info: formData.contact_info,
                    subscription_plan: formData.subscription_plan,
                    max_users_per_branch: formData.max_users,
                    branches: branches.filter(b => b.name && b.admin_email) // Only valid branches
                };
                response = await superAdminAPI.createSchoolGroup(groupData);
            }

            onSchoolCreated(response);

            // Reset form
            setFormData({
                name: '',
                domain: '',
                subscription_plan: 'trial',
                max_users: 50,
                address: {
                    city: '',
                    state: '',
                    country: '',
                    postal_code: '',
                    street_address: ''
                },
                contact_info: {
                    email: '',
                    phone: '',
                    contact_person: ''
                },
                admin_name: '',
                admin_email: ''
            });
            setBranches([]);
            setSchoolType('single');
            setStep(0);
        } catch (error) {
            console.error("Error creating school:", error);
            setError(superAdminAPI.handleError(error));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {step === 0 ? 'Select School Type' :
                            schoolType === 'group' ? 'Create School Group' : 'Create New School'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6">
                    {/* Progress Indicator - Only show for steps > 0 */}
                    {step > 0 && (
                        <div className="mb-8">
                            <div className="flex items-center justify-between mb-2">
                                {Array.from({ length: getTotalSteps() }, (_, i) => i + 1).map((s) => (
                                    <div
                                        key={s}
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                                            ${s < step ? 'bg-green-500 text-white' :
                                                s === step ? 'bg-orange-500 text-white' :
                                                    'bg-gray-200 text-gray-600'}`}
                                    >
                                        {s}
                                    </div>
                                ))}
                            </div>
                            <div className="relative">
                                <div className="absolute top-0 left-0 h-1 bg-gray-200 w-full"></div>
                                <div
                                    className="absolute top-0 left-0 h-1 bg-orange-500"
                                    style={{ width: `${((step - 1) / (getTotalSteps() - 1)) * 100}%` }}
                                ></div>
                            </div>
                            <div className="flex justify-between mt-2 text-xs text-gray-600">
                                <span>{schoolType === 'group' ? 'Group Details' : 'School Details'}</span>
                                <span>Contact Information</span>
                                {schoolType === 'group' && <span>Branches</span>}
                                <span>Admin Setup</span>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div
                            className="mb-6 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm flex items-start gap-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                            <div>{error}</div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Step 0: School Type Selection */}
                        {step === 0 && (
                            <div className="space-y-6">
                                <div className="text-center mb-6">
                                    <h3 className="text-lg font-medium text-gray-800">What type of school would you like to create?</h3>
                                    <p className="text-sm text-gray-500 mt-1">Choose based on your organization structure</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Single School Option */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSchoolType('single');
                                            setStep(1);
                                        }}
                                        className={`p-6 border-2 rounded-xl text-left transition-all hover:shadow-lg
                                            ${schoolType === 'single' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}
                                    >
                                        <Building2 size={32} className="text-orange-500 mb-3" />
                                        <h4 className="font-semibold text-gray-800">Single School</h4>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Create a standalone school with its own admin and users.
                                        </p>
                                    </button>

                                    {/* School Group Option */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSchoolType('group');
                                            setStep(1);
                                        }}
                                        className={`p-6 border-2 rounded-xl text-left transition-all hover:shadow-lg
                                            ${schoolType === 'group' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}
                                    >
                                        <GitBranch size={32} className="text-orange-500 mb-3" />
                                        <h4 className="font-semibold text-gray-800">School Group</h4>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Create a parent organization with multiple branch schools.
                                        </p>
                                    </button>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mt-6">
                                    <div className="flex items-start gap-2">
                                        <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-blue-700">
                                            <p className="font-medium">Not sure which to choose?</p>
                                            <p className="mt-1">
                                                Choose <strong>Single School</strong> for independent institutions.
                                                Choose <strong>School Group</strong> for chains or organizations with multiple branches.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 1: School Details */}
                        {step === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        School Name*
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., ABC International School"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Domain Prefix*
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="text"
                                            name="domain"
                                            value={formData.domain}
                                            onChange={handleChange}
                                            className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            placeholder="e.g., abcschool"
                                        />
                                        <span
                                            className="bg-gray-100 p-3 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                                            .edumonics.com
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        This will be used as the subdomain for school access
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Subscription Plan
                                    </label>
                                    <select
                                        name="subscription_plan"
                                        value={formData.subscription_plan}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    >
                                        <option value="trial">Trial</option>
                                        <option value="basic">Basic</option>
                                        <option value="premium">Premium</option>
                                        <option value="enterprise">Enterprise</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Maximum Users
                                    </label>
                                    <input
                                        type="number"
                                        name="max_users"
                                        value={formData.max_users}
                                        onChange={handleChange}
                                        min="1"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 2: Contact Information */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Person*
                                    </label>
                                    <input
                                        type="text"
                                        name="contact_info.contact_person"
                                        value={formData.contact_info.contact_person}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., John Smith"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Email*
                                    </label>
                                    <input
                                        type="email"
                                        name="contact_info.email"
                                        value={formData.contact_info.email}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., contact@school.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Phone
                                    </label>
                                    <input
                                        type="text"
                                        name="contact_info.phone"
                                        value={formData.contact_info.phone}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., +1234567890"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            name="address.city"
                                            value={formData.address.city}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State/Province
                                        </label>
                                        <input
                                            type="text"
                                            name="address.state"
                                            value={formData.address.state}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            name="address.country"
                                            value={formData.address.country}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Postal Code
                                        </label>
                                        <input
                                            type="text"
                                            name="address.postal_code"
                                            value={formData.address.postal_code}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Admin Setup */}
                        {step === 3 && (
                            <div className="space-y-4">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6">
                                    <h3 className="text-blue-800 font-medium text-sm mb-1 flex items-center gap-2">
                                        <Info size={18} />
                                        School Admin Setup
                                    </h3>
                                    <p className="text-blue-700 text-sm">
                                        Create an admin user for this school. The admin will receive an email with
                                        temporary login credentials and will be guided through an account activation
                                        process.
                                    </p>
                                </div>

                                <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 mb-6">
                                    <h3 className="text-orange-800 font-medium text-sm mb-1 flex items-center gap-2">
                                        <HelpCircle size={18} />
                                        How It Works
                                    </h3>
                                    <ol className="text-orange-700 text-sm space-y-2 list-decimal pl-4">
                                        <li>
                                            A temporary password will be generated and sent to the admin's email.
                                        </li>
                                        <li>
                                            The admin will login with the temporary password and be prompted to verify
                                            their email with an OTP.
                                        </li>
                                        <li>
                                            After verification, they'll set their permanent password and gain access to
                                            the school admin panel.
                                        </li>
                                    </ol>
                                </div>

                                <div className="flex gap-3 items-center">
                                    <User size={20} className="text-gray-400" />
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Admin Name*
                                        </label>
                                        <input
                                            type="text"
                                            name="admin_name"
                                            value={formData.admin_name}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            placeholder="e.g., John Smith"
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-3 items-center">
                                    <Mail size={20} className="text-gray-400" />
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Admin Email*
                                        </label>
                                        <input
                                            type="email"
                                            name="admin_email"
                                            value={formData.admin_email}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            placeholder="e.g., admin@school.com"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            A temporary password will be generated and sent to this email.
                                        </p>
                                    </div>
                                </div>

                                <div
                                    className="mt-6 p-4 bg-green-50 border border-green-100 rounded-lg flex items-start gap-2">
                                    <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-green-700">
                                        <p className="font-medium">Ready to create this {schoolType === 'group' ? 'school group' : 'school'}?</p>
                                        <p className="mt-1">
                                            By clicking "Create {schoolType === 'group' ? 'Group' : 'School'}," you'll create this {schoolType === 'group' ? 'group with all branches' : 'school with all the details provided'}.
                                            The admin will receive an email with instructions to activate their account.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3 (Group Mode): Branch Schools */}
                        {step === 3 && schoolType === 'group' && (
                            <div className="space-y-4">
                                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 mb-6">
                                    <h3 className="text-purple-800 font-medium text-sm mb-1 flex items-center gap-2">
                                        <GitBranch size={18} />
                                        Branch Schools
                                    </h3>
                                    <p className="text-purple-700 text-sm">
                                        Add branch schools to this group. Each branch will have its own admin and isolated database.
                                        You can add more branches later.
                                    </p>
                                </div>

                                {branches.length === 0 && (
                                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                        <GitBranch size={40} className="text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-500">No branches added yet</p>
                                        <p className="text-sm text-gray-400 mt-1">Click the button below to add a branch school</p>
                                    </div>
                                )}

                                {branches.map((branch, index) => (
                                    <div key={index} className="p-4 border border-gray-200 rounded-lg relative">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="font-medium text-gray-700">Branch #{index + 1}</h4>
                                            <button
                                                type="button"
                                                onClick={() => removeBranch(index)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Branch Name*</label>
                                                <input
                                                    type="text"
                                                    value={branch.name}
                                                    onChange={(e) => updateBranch(index, 'name', e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                                    placeholder="e.g., North Campus"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Domain Prefix</label>
                                                <input
                                                    type="text"
                                                    value={branch.domain}
                                                    onChange={(e) => updateBranch(index, 'domain', e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                                    placeholder="e.g., north"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Name*</label>
                                                <input
                                                    type="text"
                                                    value={branch.admin_name}
                                                    onChange={(e) => updateBranch(index, 'admin_name', e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                                    placeholder="Branch admin name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Email*</label>
                                                <input
                                                    type="email"
                                                    value={branch.admin_email}
                                                    onChange={(e) => updateBranch(index, 'admin_email', e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                                    placeholder="admin@branch.com"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addBranch}
                                    className="w-full p-3 border-2 border-dashed border-orange-300 rounded-lg text-orange-500 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Plus size={18} />
                                    Add Branch School
                                </button>
                            </div>
                        )}

                        <div className="mt-8 flex justify-between">
                            {step > 0 ? (
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Back
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                            )}

                            {/* Don't show Next on Step 0 since selection auto-advances */}
                            {step > 0 && step < getTotalSteps() ? (
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                                >
                                    Next
                                </button>
                            ) : step === getTotalSteps() ? (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader size={16} className="animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        schoolType === 'group' ? 'Create School Group' : 'Create School'
                                    )}
                                </button>
                            ) : null}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SchoolCreationModal;